import type { ExpressionValue } from '@shared/models/project.model';
import type { SelectableQuantityUnit } from '@shared/models/unit.model';
import { createExpression } from '@shared/helpers/expression-factory.helpers';

// ── Shared state type ─────────────────────────────────────────────────────────

/**
 * The three fields that define a meaningful change in an {@link ExpressionInput}:
 * the numeric value override, the expression formula, and the unit.
 * Used as the canonical shape for both the *current* and the *next* state when
 * classifying what the user actually changed.
 */
export type ExpressionInputState = {
  /** Numeric value override. `undefined` means the value has been cleared. */
  value: number | undefined;
  /** Formula string. `undefined` OR empty string means the expression has been cleared. */
  expression: string | undefined;
  /** Unit. `undefined` means the unit has no changes */
  unit: SelectableQuantityUnit | undefined;
};

// ── Event types ──────────────────────────────────────────────────────────────

/**
 * User typed or changed the numeric value override in the input.
 * {@link ExpressionInputState.expression} is the current formula (unchanged);
 * {@link ExpressionInputState.value} is the new override.
 */
export type ValueChangeEvent = {
  type: 'value-change';
  count: ExpressionInputState;
};

/**
 * User cleared the numeric value override (input erased), while the property
 * still has a meaningful expression formula that should continue to drive the
 * resolved value.
 */
export type ValueClearEvent = {
  type: 'value-clear';
};

/** User selected a different unit via the unit-picker button. */
export type UnitChangeEvent = {
  type: 'unit-change';
  unit: SelectableQuantityUnit;
};

/**
 * User saved a new expression formula via the fx-panel, and also changed
 * the unit in the same panel session.
 */
export type ExpressionAndUnitChangeEvent = {
  type: 'expression-and-unit-change';
  count: ExpressionInputState;
  unit: SelectableQuantityUnit;
};

/**
 * User saved a new expression formula via the fx-panel (unit unchanged).
 */
export type ExpressionChangeEvent = {
  type: 'expression-change';
  count: ExpressionInputState;
};

/**
 * The expression was fully removed, either by:
 * - Clearing the expression field in the fx-panel, OR
 * - Clearing the number input when expression is effectively empty (`''` or `'0'`)
 *   and a fallback exists to revert to — the caller normalises this case before
 *   invoking the classifier (DEV-3220).
 *
 * The handler should set `count = undefined` so the fallback takes over.
 */
export type ExpressionClearEvent = {
  type: 'expression-clear';
};

/** Discriminated union covering every possible user interaction on an {@link ExpressionInput}. */
export type ExpressionInputChangeEvent =
  | ValueChangeEvent
  | ValueClearEvent
  | UnitChangeEvent
  | ExpressionAndUnitChangeEvent
  | ExpressionChangeEvent
  | ExpressionClearEvent;

// ── Classifier ───────────────────────────────────────────────────────────────

/**
 * Classifies the delta between two {@link ExpressionInputState} snapshots into
 * a typed {@link ExpressionInputChangeEvent}.
 *
 * **Callers are responsible for pre-normalisation** before building `next`:
 * - Empty expressions (`''`) should be represented as `expression: undefined`.
 * - DEV-3220: when clearing a numeric override on a trivially-zero expression
 *   (`'0'`) while a fallback exists, callers should also set
 *   `expression: undefined` so the classifier emits `expression-clear`.
 *
 * Rules (evaluated in priority order):
 * 1. **expression-clear** — `next.expression` is `undefined` or `''`.
 * 2. **value-change** — `next.value` is set and differs from `current.value`.
 * 3. **value-clear** — `next.value` is `undefined` while `current.value` was set.
 * 4. **expression-and-unit-change** — formula changed AND unit changed.
 * 5. **expression-change** — only the formula changed.
 * 6. **unit-change** — only the unit changed.
 *
 * @param current - Snapshot of the input state before the user's action.
 * @param next - Snapshot of the input state after the user's action.
 */
export const classifyExpressionInputChange = (
  current: ExpressionInputState,
  next: ExpressionInputState,
): ExpressionInputChangeEvent => {
  // A defined, non-same unit is the only kind of unit change that matters.
  const changedUnit =
    next.unit !== undefined && next.unit !== current.unit
      ? next.unit
      : undefined;

  // Rule 1: expression-clear — next has no expression
  if (!next.expression) return { type: 'expression-clear' };

  // Rule 2: value-change — user typed a new number override
  if (next.value !== undefined && next.value !== current.value) {
    return { type: 'value-change', count: next };
  }

  // Rule 3: value-clear — user erased the number override, expression still live
  if (next.value === undefined && current.value !== undefined) {
    return { type: 'value-clear' };
  }

  // Rule 4 & 5: expression formula changed (from fx-panel)
  const expressionChanged = next.expression !== current.expression;
  if (expressionChanged) {
    if (changedUnit !== undefined) {
      return {
        type: 'expression-and-unit-change',
        count: next,
        unit: changedUnit,
      };
    }
    return { type: 'expression-change', count: next };
  }

  // Rule 6: only the unit changed
  if (changedUnit !== undefined) {
    return { type: 'unit-change', unit: changedUnit };
  }

  // Fallback (should not normally be reached — isEqual guards prevent no-op emissions).
  return { type: 'expression-change', count: next };
};

// ── Conversion helper ─────────────────────────────────────────────────────────

/**
 * Rebuilds a full {@link ExpressionValue} from a classifier state snapshot.
 *
 * Uses `existing` as the source for `resolved`/`formatted` when they cannot be
 * derived from `state` alone (e.g. after an expression-formula change). The
 * pipeline recomputes the correct resolved value once the change is applied.
 */
export const toExpressionValue = (
  state: ExpressionInputState,
  existing: ExpressionValue | undefined,
): ExpressionValue =>
  createExpression(
    {
      expression: state.expression ?? existing?.expression ?? '',
      value: state.value,
    },
    // Value overrides keep inherit/formula `resolved` until the pipeline recomputes (DEV-3313).
    state.value !== undefined
      ? (existing?.resolved ?? state.value)
      : (existing?.resolved ?? 0),
  );
